import ExcelJS from 'exceljs'
import QRCode from 'qrcode'
import api from '../api/client'
import { gregorianToJalali } from './utils'

/** دریافت لینک عمومی ۳۰ روزهٔ گزارش + تبدیل به کیوآرکد (data-URI) */
export async function fetchReportQr(contractId) {
  try {
    const res = await api.get(`contracts/${contractId}/report/qr-link/`)
    const url = res.data && res.data.url
    if (!url) return null
    return await QRCode.toDataURL(url, {
      width: 220,
      margin: 1,
      color: { dark: '#101a4d', light: '#ffffff' },
    })
  } catch (e) {
    return null
  }
}

/* ─────────────── رنگها و کمکیها ─────────────── */
const NAVY = 'FF1A237E'      // سرمه‌ای تیره (تیتر/هدر)
const SECTION_BG = 'FF3949AB' // سرمه‌ای روشنتر (تیتر بخشها)
const ZEBRA = 'FFF3F5FC'     // ردیفهای زوج
const TOTAL_BG = 'FFFFF3CD'  // ردیف جمع (طلایی)
const TOTAL_FONT = 'FF8D6E00'

const thin = {
  top: { style: 'thin', color: { argb: 'FFB9C2E8' } },
  left: { style: 'thin', color: { argb: 'FFB9C2E8' } },
  bottom: { style: 'thin', color: { argb: 'FFB9C2E8' } },
  right: { style: 'thin', color: { argb: 'FFB9C2E8' } },
}

function styleHeaderRow(ws, rowIdx, colCount) {
  const row = ws.getRow(rowIdx)
  for (let c = 1; c <= colCount; c++) {
    const cell = row.getCell(c)
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } }
    cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } }
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    cell.border = thin
  }
  row.height = 24
}

function styleCaptionRow(ws, rowIdx, colCount, text, bg = SECTION_BG) {
  ws.mergeCells(rowIdx, 1, rowIdx, colCount)
  const cell = ws.getCell(rowIdx, 1)
  cell.value = text
  cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
  cell.alignment = { horizontal: 'right', vertical: 'middle' }
  ws.getRow(rowIdx).height = 22
}

function styleDataRow(ws, rowIdx, colCount, zebra) {
  const row = ws.getRow(rowIdx)
  for (let c = 1; c <= colCount; c++) {
    const cell = row.getCell(c)
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: false }
    cell.border = thin
    if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } }
  }
}

function styleTotalRow(ws, rowIdx, colCount) {
  const row = ws.getRow(rowIdx)
  for (let c = 1; c <= colCount; c++) {
    const cell = row.getCell(c)
    cell.font = { bold: true, size: 11, color: { argb: TOTAL_FONT } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_BG } }
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = {
      top: { style: 'double', color: { argb: TOTAL_FONT } },
      left: thin.left, right: thin.right, bottom: thin.bottom,
    }
  }
  row.height = 22
}

/** درج کیوآرکد data-URI — واقعاً داخل فایل اکسل (تصویر PNG) */
function addQrImage(wb, ws, qrDataUri, colCount) {
  if (!qrDataUri) return
  try {
    const imgId = wb.addImage({ base64: qrDataUri, extension: 'png' })
    // در RTL: ستون آخر (سمت راست) — بالای برگه کنار تیتر
    ws.addImage(imgId, {
      tl: { col: Math.max(0, colCount - 2.6), row: 0.2 },
      ext: { width: 96, height: 96 },
    })
  } catch (e) {
    // QR اختیاری است — خروجی بدون QR ادامه می‌دهد
  }
}

async function saveWorkbook(wb, filename) {
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60000)
  return true
}

/** ردیفهای استاندارد بالای هر گزارش: تاریخ + یادداشت کیوآرکد ۳۰ روزه */
export function reportMetaNotes() {
  let today = ''
  try {
    today = gregorianToJalali(new Date().toISOString().slice(0, 10))
  } catch (e) {
    today = ''
  }
  return [
    'تاریخ گزارش: ' + today,
    'با اسکن کیوآرکد، این گزارش تا ۳۰ روز در موبایل قابل مشاهده است.',
  ]
}

function writeTitleAndNotes(ws, { title, notes }, colCount) {
  let r = 1
  if (title) {
    styleCaptionRow(ws, r, colCount, title, NAVY)
    ws.getRow(r).height = 30
    ws.getRow(r).alignment = { horizontal: 'center', vertical: 'middle' }
    r += 1
  }
  if (notes && notes.length) {
    notes.forEach((n) => {
      ws.mergeCells(r, 1, r, colCount)
      const c = ws.getCell(r, 1)
      c.value = n
      c.font = { size: 10, italic: true, color: { argb: 'FF607D8B' } }
      c.alignment = { horizontal: 'right', vertical: 'middle' }
      r += 1
    })
    r += 1 // ردیف فاصله
  }
  return r
}

function setColumnWidths(ws, headers) {
  headers.forEach((h, i) => {
    ws.getColumn(i + 1).width = Math.min(38, Math.max(10, String(h || '').length + 5))
  })
}

/**
 * ساخت و دانلود فایل اکسل شکیل — عنوان رنگی، هدر سرمه‌ای، ردیفهای یک‌درمیان،
 * ردیف جمع طلایی (اختیاری: totalRow) و کیوآرکد واقعی داخل فایل.
 */
export async function exportExcel({ filename, headers, rows, title, notes = [], qrDataUri = null, totalRow = null }) {
  const wb = new ExcelJS.Workbook()
  wb.views = [{ rightToLeft: true }]
  const ws = wb.addWorksheet('گزارش', {
    views: [{ rightToLeft: true, showGridLines: false }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
  })

  const colCount = headers.length
  let r = writeTitleAndNotes(ws, { title, notes }, colCount)

  // هدر ستونها
  ws.getRow(r).values = headers
  styleHeaderRow(ws, r, colCount)
  r += 1

  // داده‌ها
  rows.forEach((row, i) => {
    ws.getRow(r).values = row
    styleDataRow(ws, r, colCount, i % 2 === 1)
    r += 1
  })

  // ردیف جمع (اختیاری)
  if (totalRow) {
    ws.getRow(r).values = totalRow
    styleTotalRow(ws, r, colCount)
    r += 1
  }

  setColumnWidths(ws, headers)
  addQrImage(wb, ws, qrDataUri, colCount)

  return await saveWorkbook(wb, filename)
}

/**
 * نسخهٔ چندبخشی — هر بخش: { caption, headers, rows, totalRow? }
 * (قالب مشابه صورت‌وضعیت: تیتر رنگی، هدر سرمه‌ای، ردیفهای یک‌درمیان، جمع طلایی)
 */
export async function exportExcelMulti({ filename, title, notes = [], sections = [], qrDataUri = null }) {
  const wb = new ExcelJS.Workbook()
  wb.views = [{ rightToLeft: true }]
  const ws = wb.addWorksheet('گزارش', {
    views: [{ rightToLeft: true, showGridLines: false }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
  })

  const colCount = Math.max(...sections.map((s) => (s.headers ? s.headers.length : 1)), 1)
  let r = writeTitleAndNotes(ws, { title, notes }, colCount)

  sections.forEach((sec, idx) => {
    if (idx > 0) r += 1 // فاصله بین بخشها
    styleCaptionRow(ws, r, colCount, sec.caption || '')
    r += 1
    if (sec.headers) {
      ws.getRow(r).values = sec.headers
      styleHeaderRow(ws, r, colCount)
      r += 1
    }
    (sec.rows || []).forEach((row, i) => {
      ws.getRow(r).values = row
      styleDataRow(ws, r, colCount, i % 2 === 1)
      r += 1
    })
    if (sec.totalRow) {
      ws.getRow(r).values = sec.totalRow
      styleTotalRow(ws, r, colCount)
      r += 1
    }
  })

  setColumnWidths(ws, (sections[0] && sections[0].headers) || [])
  addQrImage(wb, ws, qrDataUri, colCount)

  return await saveWorkbook(wb, filename)
}
